import { useCallback, useMemo } from "react"
import { useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import { useGetDepartmentsQuery } from "shared/redux/endpoints/procurement"
import { useGetProcurementCentersQuery } from "shared/redux/endpoints/procurementItems"
import { REGION_OPTIONS, URGENCY_OPTIONS } from "../../constants"
import { departmentList } from "../payment/create/NonPoPaymentBuilder"

// Returns a lookup from a condition field to its selectable values, or null for
// a free-text field.
const useConditionValueOptions = () => {
  // Real budget categories (real numeric `id`, not the hand-picked slug list
  // used elsewhere for report/list filters) — a "category" condition must
  // compare against the actual budget_category.id the backend evaluates
  // rules with.
  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  )
  // String ids: "in" values round-trip as one comma-joined string.
  const { data: departmentsRaw } = useGetDepartmentsQuery()
  const departmentOptions = useMemo(
    () =>
      departmentList(departmentsRaw).map((d) => ({
        value: String(d.teamId),
        label: d.region ? `${d.teamName} (${d.region})` : d.teamName,
      })),
    [departmentsRaw]
  )
  const { data: centersPage } = useGetProcurementCentersQuery({ size: 200 })
  const centerOptions = useMemo(
    () =>
      (centersPage?.items || []).map((c) => ({
        value: String(c.centerId),
        label: c.sunCenterCode ? `${c.centerName} (${c.sunCenterCode})` : c.centerName,
      })),
    [centersPage]
  )

  // Values here must match what the entity actually sends, not just read
  // nicely — Order Request submits urgency uppercased (see
  // OrderRequestFormContainer: `form.urgency.toUpperCase()`), so "urgent"
  // never matches a saved condition unless the option value is "URGENT".
  return useCallback(
    (field) => {
      if (field === "region") return REGION_OPTIONS
      if (field === "category") return categoryOptions
      if (field === "department") return departmentOptions
      if (field === "center") return centerOptions
      if (field === "urgency")
        return URGENCY_OPTIONS.map((o) => ({ value: o.value.toUpperCase(), label: o.label }))
      return null
    },
    [categoryOptions, departmentOptions, centerOptions]
  )
}

export default useConditionValueOptions
